
import { useState } from "react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useToast } from "@/components/ui/use-toast";
import { handleError } from "@/utils/error-handling";

export const useGoogleAuth = () => {
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    
    // First, show a reassuring toast
    toast({
      title: "Redirecting to Google",
      description: "You'll be securely redirected to sign in with Google",
      duration: 3000,
    });
    
    try {
      // Get the current site URL for the redirect
      const currentSiteUrl = window.location.origin;
      console.log("Current site URL for Google OAuth redirect:", currentSiteUrl);
      
      // Check if we're on localhost and warn in console (not to user)
      if (currentSiteUrl.includes('localhost')) {
        console.warn('Running on localhost - make sure this redirect URL is configured in your Supabase project');
      }
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: currentSiteUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      });
      
      if (error) throw error;
      
    } catch (error: any) {
      console.error('Error logging in with Google:', error);
      setError(error.message || "Could not sign in with Google");
      handleError(error, "Could not sign in with Google");
      
      toast({
        title: "Authentication Failed",
        description: error.message || "Could not sign in with Google. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    setError,
    handleGoogleLogin
  };
};
